import { useState, useEffect, useCallback } from 'react';
import type { Product } from '../types/product';
import { isOfferActive } from '../types/product';
import { fetchAllProducts } from '../services/productsApi';

// ─── Module-level cache ────────────────────────────────────────────────────────
// The Navbar mounts on every route, so we must NOT re-scan the whole catalog on
// each navigation just to decide whether the "Ofertas" link should appear. We
// fetch once, cache the resulting offers list, and share it across every caller
// (Navbar + offers page). A short TTL keeps prices/validity reasonably fresh.

interface OffersCache {
  offers: Product[];
  ts: number;
}

const TTL_MS = 5 * 60 * 1000; // 5 minutes
let cache: OffersCache | null = null;
let inflight: Promise<Product[]> | null = null;

async function loadOffers(force = false): Promise<Product[]> {
  const fresh = cache !== null && Date.now() - cache.ts < TTL_MS;
  if (!force && fresh) return cache!.offers;
  if (!force && inflight) return inflight;

  inflight = fetchAllProducts({})
    .then((all) => {
      const now = new Date();
      const offers = all.filter((p) => isOfferActive(p, now));
      cache = { offers, ts: Date.now() };
      return offers;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

interface UseActiveOffersResult {
  offers: Product[];
  /** True when at least one currently-valid offer exists ("oferta vigente"). */
  hasOffers: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Returns the products with a currently-valid offer. Backed by a shared cache so
 * mounting this hook in many places (Navbar + offers page) triggers a single
 * catalog scan. Use `hasOffers` to conditionally render the offers entry point.
 */
export function useActiveOffers(): UseActiveOffersResult {
  const [offers, setOffers] = useState<Product[]>(cache?.offers ?? []);
  const [loading, setLoading] = useState<boolean>(cache === null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (force = false) => {
    const fresh = cache !== null && Date.now() - cache.ts < TTL_MS;
    if (!fresh) setLoading(true);
    setError(null);
    try {
      const data = await loadOffers(force);
      setOffers(data);
    } catch (err) {
      setError('Error al cargar ofertas');
      console.error('[useActiveOffers]', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return {
    offers,
    hasOffers: offers.length > 0,
    loading,
    error,
    refresh: () => load(true),
  };
}
